<script setup lang="ts">
// Single-number filter. Wraps `<input type="number">`. Empty input
// emits `null` (the unset state) so SQL authors can branch on
// "no threshold applied" without sentinel values.

import { computed, useId } from "vue";

import type { NumberOpts } from "../sbi/protocol";
import { usePrintMode } from "../sbi/print-mode.ts";
import PrintStaticControl from "./PrintStaticControl.vue";
import ControlLabel from "./ControlLabel.vue";

const props = defineProps<{
  id: string;
  value: number | null;
  opts?: NumberOpts;
}>();

const emit = defineEmits<{
  (e: "change-target", id: string, value: number | null): void;
}>();

const isPrint = usePrintMode();

// Label wiring: ControlLabel puts `labelId` on the label text and points
// its `<label for>` at `controlId`.
const labelId = useId();
const controlId = useId();

// Print-mode display: the raw number, or a placeholder when unset.
// We don't apply `opts?.format` here — the live number input doesn't
// format either, so the printed value should match what the GM saw.
const printValue = computed(() =>
  props.value !== null && props.value !== undefined ? String(props.value) : "(not set)",
);

function onInput(e: Event): void {
  const raw = (e.target as HTMLInputElement).value;
  if (raw === "") {
    emit("change-target", props.id, null);
    return;
  }
  const n = Number(raw);
  if (Number.isNaN(n)) return;
  emit("change-target", props.id, n);
}
</script>

<template>
  <PrintStaticControl
    v-if="isPrint()"
    :label="opts?.label"
    :value="printValue"
  />
  <div v-else class="number">
    <ControlLabel
      v-if="opts?.label || opts?.description"
      class="number-label"
      :label="opts?.label"
      :description="opts?.description"
      :for-id="controlId"
      :label-id="labelId"
    />
    <input
      :id="controlId"
      type="number"
      :value="value ?? ''"
      :min="opts?.min"
      :max="opts?.max"
      :step="opts?.step"
      :placeholder="opts?.placeholder"
      @input="onInput"
    />
  </div>
</template>

<style scoped>
/* Never wider than the slot it sits in; full width when that slot is
   narrow (filter bar < 480px, a narrow column cell). */
.number {
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
@container (max-width: 479px) {
  .number {
    display: flex;
    width: 100%;
  }
}
.number-label {
  color: var(--color-text-muted);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
input[type="number"] {
  background: var(--card);
  color: var(--card-ink);
  border: 1px solid var(--rule);
  border-radius: var(--radius);
  padding: 6px 10px;
  font: inherit;
  width: 8ch;
  max-width: 100%;
}
input[type="number"]:focus {
  outline: 2px solid var(--primary);
  outline-offset: 1px;
}
/* Touch: 16px text (iOS zooms the page when a smaller input takes
   focus) and 40–44px targets. Desktop keeps the 13px density. */
@media (pointer: coarse) {
  input[type="number"] {
    min-height: 44px;
    font-size: 16px;
  }
}
</style>
